import "./style.scss";

import { Close } from "@mui/icons-material";
import CloseIcon from "@mui/icons-material/Close";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import { Modal, Skeleton } from "@mui/material";
import { Tooltip } from "@mui/material";
import PDF from "assets/img/svg/pdf";
import SendEmailIcon from "assets/img/svg/sendEmail";
import classNames from "classnames";
import { getCleanLanguageCode } from "components/Beneficiar/utils";
import ModalBox from "components/Modal";
import { PortalType } from "contexts/portal.context";
import { getStatementFilename } from "helpers/utils";
import { useCompanySettings } from "hooks/useCompanySettings";
import type { ChangeEvent, KeyboardEvent } from "react";
import { useContext, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { fetchBeneficiar } from "store/beneficiaryStore";
import type { GeneralTabTerm } from "store/managementStore";
import { ITerm } from "store/managementStore";
import { EnumCompanySettings, fetchCompanyInfo } from "store/settingsStore";
import type { AppDispatch, RootState } from "store/store";
import { useAppSelector } from "store/store";
import { EStatementType } from "types";

import StyledButton from "../StyledButton";
import { buildBulkBodyByLang, buildBulkSubjectByLang, getEmailContent, getEmailSubject } from "./helpers";

export type TermData = {
    beneficiaryId?: number;
    author: string;
    title: string;
    email?: string;
    precount: string;
    contributions?: number;
    contractSigned?: number | null;
    issue?: string;
};

interface SendEmailProps {
    isModalOpen: boolean;
    onClose: () => void;
    termData?: TermData;
    showGross?: boolean;
    sendEmail: (
        emails: string[],
        subject: string,
        body: string,
        ignore?: EStatementType[],
        subjectByLang?: Record<string, string>,
        bodyByLang?: Record<string, string>,
    ) => void;
    hasAuthorPortalAccess?: boolean;
    isBulk?: boolean;
    isPress?: boolean;
    haveNoEmail?: GeneralTabTerm[];
    isStatementCurrencyDefault?: boolean;
    hasCollectiveManagement?: boolean;
}

const SendEmail = (props: SendEmailProps) => {
    const {
        isModalOpen,
        onClose,
        termData = {
            beneficiaryId: -1,
            author: "",
            title: "",
            email: "",
            precount: "",
            contractSigned: null,
            issue: "",
        },
        showGross = true,
        sendEmail,
        hasAuthorPortalAccess = false,
        isBulk = false,
        isPress,
        haveNoEmail,
        isStatementCurrencyDefault = true,
        hasCollectiveManagement = false,
    } = props;

    const [t] = useTranslation();
    const dispatch = useDispatch<AppDispatch>();
    const { companySettings } = useSelector((state: RootState) => state.settings);
    const { companyLanguage, countryFeatures } = useCompanySettings();
    const { showNet, showPrecount } = countryFeatures.statementBlocks;
    const { beneficiar, beneficiaryStatus } = useAppSelector((state) => state.beneficiary);

    const portalType = useContext(PortalType);

    const emailLanguage = (isBulk ? companyLanguage : getCleanLanguageCode(beneficiar.lang)) ?? "fr";

    const defaultEmail = useMemo(() => {
        return companySettings?.settings.find(
            (s) => s.name === EnumCompanySettings[`termMailTemplate${emailLanguage.toUpperCase()}`],
        );
    }, [companySettings, beneficiar]);

    const [emails, setEmails] = useState<string[]>(defaultEmail?.content?.mailCC ?? []);
    const [hasCC, setHasCC] = useState(!!defaultEmail?.content?.mailCC);
    const [mailInput, setMailInput] = useState("");

    /**
     * If a document needs to be ignore, include it in this array.
     */
    const [ignore, setIgnore] = useState<EStatementType[]>([]);

    const emailSubject = useMemo(() => {
        return getEmailSubject({
            defaultSubject: defaultEmail?.content.subject,
            isBulk,
            termData,
            beneficiar,
            lang: emailLanguage,
        });
    }, [termData, beneficiar, emailLanguage]);

    const emailContent = useMemo(() => {
        return getEmailContent({
            defaultBody: defaultEmail?.content?.body,
            isBulk,
            termData,
            beneficiar,
            portalType,
            companySettings,
            lang: emailLanguage,
        });
    }, [termData, beneficiar, emailLanguage]);

    /**
     * In bulk mode the backend picks per beneficiary language from these maps
     * so every recipient receives the email in their own language.
     */
    const subjectByLang = useMemo(
        () => buildBulkSubjectByLang({ defaultSubject: defaultEmail?.content?.subject }),
        [defaultEmail],
    );

    const bodyByLang = useMemo(
        () =>
            buildBulkBodyByLang({
                defaultBody: defaultEmail?.content?.body,
                portalType,
                companyName: companySettings?.name ?? "",
            }),
        [defaultEmail, companySettings, portalType],
    );

    const [subjectInput, setSubjectInput] = useState(emailSubject ?? "");
    const [bodyInput, setBodyInput] = useState(emailContent ?? "");

    useEffect(() => {
        emailSubject && setSubjectInput(emailSubject);
    }, [emailSubject]);

    useEffect(() => {
        emailContent && setBodyInput(emailContent);
    }, [emailContent]);

    const addEmail = (email: string) => {
        const emailRegex = /^[\w-.]+@([\w-]+\.)+[\w-]{2,4}$/;
        if (!emailRegex.test(email)) return;
        setEmails((emails) => Array.from(new Set([...emails, email])));
        setMailInput("");
    };

    const handleAddMail = (e: ChangeEvent<HTMLInputElement>) => {
        setMailInput(e.target.value);
        if (e.target.value.indexOf(",") < 0) return;
        addEmail(e.target.value.replace(/ /g, "").slice(0, -1));
    };

    const handleAddMailWithEnter = (e: KeyboardEvent<HTMLInputElement>) =>
        (e.key == "Enter" || e.code == "Space") && addEmail((e.target as HTMLInputElement).value.replace(/ /g, ""));

    const handleAddMailWithBlur = (e: any) => addEmail(e.target.value);

    const handleRemoveMail = (index: number) => {
        setEmails((emails) => [...emails.filter((item, i) => i != index)]);
    };

    const handleCCButton = () => {
        if (hasCC) {
            setEmails([]);
            setHasCC(false);
        } else {
            setEmails(defaultEmail?.content?.mailCC ?? []);
            setHasCC(true);
        }
    };

    const formatTooltip = (names: string[] | undefined) => {
        return names?.join(", ");
    };

    useEffect(() => {
        if (!companySettings) dispatch(fetchCompanyInfo());
    }, [companySettings, dispatch]);

    useEffect(() => {
        const beneficiaryId = termData.beneficiaryId;
        if (!beneficiaryId || beneficiaryId <= 0) return;
        // If beneficiar not fetched, but termData is there.
        if (!beneficiar.id) {
            dispatch(fetchBeneficiar(beneficiaryId));
        } else if (beneficiar.id !== beneficiaryId) {
            dispatch(fetchBeneficiar(beneficiaryId));
        }
    }, []);

    const beneficiaryLoading = !isBulk && ["idle", "pending"].includes(beneficiaryStatus);

    return (
        <>
            {hasAuthorPortalAccess ? (
                <ModalBox open={isModalOpen} onClose={onClose} mainTitle={t("Summary.SendToBeneficiarPortal")}>
                    <div className="send-email-portal-modal">
                        <div className="send-email-portal-modal-body">{t("Summary.PortalSendMessage")}</div>
                        <div className="send-email-portal-modal-footer">
                            <StyledButton
                                buttonType="primary"
                                style={{ width: "100%" }}
                                onClick={() => {
                                    sendEmail(emails, subjectInput, bodyInput);
                                    setIgnore([]);
                                }}
                            >
                                {t("SendEmail.Transmit")}
                            </StyledButton>
                        </div>
                    </div>
                </ModalBox>
            ) : (
                <Modal
                    open={isModalOpen}
                    onClose={() => {
                        onClose();
                        setIgnore([]);
                        setEmails(defaultEmail?.content?.mailCC ?? []);
                    }}
                >
                    <div className="send-email">
                        {haveNoEmail && haveNoEmail?.length > 0 && (
                            <div className="selection-error">
                                <span>{t("SendEmail.MissingEmails")}</span>
                                <Tooltip title={`${formatTooltip(haveNoEmail.map((term) => term.author))}`} arrow>
                                    <div>
                                        <HelpOutlineIcon sx={{ width: "18px", height: "18px", color: "#848B4F" }} />
                                    </div>
                                </Tooltip>
                            </div>
                        )}
                        <div className="send-email-modal">
                            <div className="send-email-header">
                                <div>
                                    <div className="send-email-header-title">
                                        {isBulk ? t("SendEmail.SendEmails") : t("SendEmail.SendEmail")}
                                    </div>
                                    <div className="send-email-header-subtitle">{t("SendEmail.HeaderSubtitle")}</div>
                                </div>
                                <div
                                    className="send-email-header-cross"
                                    onClick={() => {
                                        onClose();
                                        setEmails(defaultEmail?.content?.mailCC ?? []);
                                    }}
                                >
                                    <CloseIcon />
                                </div>
                            </div>
                            <div className="send-email-body">
                                <div className="send-email-body-from">
                                    <div className="send-email-body-from-left">
                                        <div className="send-email-body-from-from">{t("SendEmail.To")} </div>
                                        {beneficiaryLoading ? (
                                            <Skeleton variant="text" width={200} />
                                        ) : (
                                            <div className="send-email-body-chip">
                                                {!isBulk ? (
                                                    <>
                                                        <div className="send-email-body-avatar">
                                                            {(termData?.author ?? "").slice(0, 1).toLocaleUpperCase()}
                                                        </div>
                                                        <div className="send-email-body-text">
                                                            {`${beneficiar?.name ?? ""} ${beneficiar?.surname ?? ""}`}
                                                            {termData?.email && ` (${termData?.email})`}
                                                        </div>
                                                    </>
                                                ) : (
                                                    <div className="send-email-body-text">
                                                        {t("SendEmail.SelectedAddresses")}
                                                    </div>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                    <div className="send-email-body-from-right">
                                        <div
                                            className={`send-email-body-from-cc${(hasCC && " active") || ""}`}
                                            onClick={handleCCButton}
                                        >
                                            Cc
                                        </div>
                                    </div>
                                </div>
                                {hasCC && (
                                    <div className="send-email-body-cc">
                                        <div className="send-email-body-cc-text">Cc: </div>
                                        <div className="send-email-body-cc-mails">
                                            {emails.length > 0 &&
                                                emails.map((item, i) => (
                                                    <div className="send-email-body-chip" key={i}>
                                                        <div className="send-email-body-avatar">
                                                            {item.slice(0, 1).toLocaleUpperCase()}
                                                        </div>
                                                        <Tooltip title={item} arrow>
                                                            <div className="send-email-body-text">
                                                                {item.split("@")[0]}
                                                            </div>
                                                        </Tooltip>
                                                        <div
                                                            className="send-email-body-close"
                                                            onClick={() => handleRemoveMail(i)}
                                                        >
                                                            <Close />
                                                        </div>
                                                    </div>
                                                ))}
                                            <input
                                                type="text"
                                                className="send-email-body-cc-mail-input"
                                                value={mailInput}
                                                onChange={handleAddMail}
                                                onKeyDown={handleAddMailWithEnter}
                                                onBlur={handleAddMailWithBlur}
                                            />
                                        </div>
                                    </div>
                                )}
                                {beneficiaryLoading ? (
                                    <Skeleton />
                                ) : (
                                    <input
                                        type="text"
                                        className="send-email-body-subject"
                                        value={subjectInput}
                                        onChange={(e) => setSubjectInput(e.target.value)}
                                    />
                                )}
                                <div
                                    className={classNames({
                                        ["send-email-body-content"]: true,
                                        ["skeleton"]: beneficiaryLoading,
                                    })}
                                >
                                    {beneficiaryLoading ? (
                                        <div>
                                            <Skeleton width={100} />
                                            <Skeleton />
                                            <Skeleton />
                                            <Skeleton />
                                        </div>
                                    ) : (
                                        <textarea
                                            className="send-email-body-content-textarea"
                                            name="email-content"
                                            id="email-content"
                                            draggable={false}
                                            value={bodyInput}
                                            onChange={(e) => setBodyInput(e.target.value)}
                                            autoFocus
                                        ></textarea>
                                    )}
                                </div>
                                <div className="send-email-body-attachments">
                                    {!ignore.includes(EStatementType.GROSS) && showGross && (
                                        <div className="send-email-body-attachment">
                                            {!isPress && (
                                                <CloseIcon
                                                    style={{
                                                        color: "grey",
                                                        width: "13px",
                                                        height: "13px",
                                                        cursor: "pointer",
                                                    }}
                                                    onClick={() => setIgnore((prev) => [...prev, EStatementType.GROSS])}
                                                />
                                            )}
                                            <PDF />
                                            {isBulk ? (
                                                <div
                                                    title={getStatementFilename(
                                                        "{{name}}",
                                                        "{{title}}",
                                                        t("SendEmail.Gross", { lng: emailLanguage }),
                                                        new Date(),
                                                    )}
                                                    className="send-email-body-attachment-filename"
                                                >
                                                    {getStatementFilename("{{name}}", "{{title}}", "Brut", new Date())}
                                                </div>
                                            ) : (
                                                <div
                                                    title={getStatementFilename(
                                                        termData?.author,
                                                        termData?.title,
                                                        t("SendEmail.Gross", { lng: emailLanguage }),
                                                        new Date(),
                                                    )}
                                                    className="send-email-body-attachment-filename"
                                                >
                                                    {getStatementFilename(
                                                        termData?.author,
                                                        termData?.title,
                                                        t("SendEmail.Gross", { lng: emailLanguage }),
                                                        new Date(),
                                                    )}
                                                </div>
                                            )}
                                            {/* <div className="send-email-body-attachment-size">2mb</div> */}
                                        </div>
                                    )}
                                    {showNet && !ignore.includes(EStatementType.NET) && (
                                        <div className="send-email-body-attachment">
                                            {!isPress && (
                                                <CloseIcon
                                                    style={{
                                                        color: "grey",
                                                        width: "13px",
                                                        height: "13px",
                                                        cursor: "pointer",
                                                    }}
                                                    onClick={() => setIgnore((prev) => [...prev, EStatementType.NET])}
                                                />
                                            )}
                                            {isBulk ? <PDF /> : isStatementCurrencyDefault && <PDF />}
                                            {isBulk ? (
                                                <div
                                                    title={getStatementFilename(
                                                        "{{name}}",
                                                        "{{title}}",
                                                        t("SendEmail.Net", { lng: emailLanguage }),
                                                        new Date(),
                                                    )}
                                                    className="send-email-body-attachment-filename"
                                                >
                                                    {getStatementFilename("{{name}}", "{{title}}", "Net", new Date())}
                                                </div>
                                            ) : (
                                                isStatementCurrencyDefault && (
                                                    <div
                                                        title={getStatementFilename(
                                                            termData?.author,
                                                            termData?.title,
                                                            t("SendEmail.Net", { lng: emailLanguage }),
                                                            new Date(),
                                                        )}
                                                        className="send-email-body-attachment-filename"
                                                    >
                                                        {getStatementFilename(
                                                            termData?.author,
                                                            termData?.title,
                                                            t("SendEmail.Net", { lng: emailLanguage }),
                                                            new Date(),
                                                        )}
                                                    </div>
                                                )
                                            )}
                                            {/* <div className="send-email-body-attachment-size">2mb</div> */}
                                        </div>
                                    )}
                                    {showPrecount &&
                                        !ignore.includes(EStatementType.PRECOUNT) &&
                                        termData?.precount != "0" &&
                                        (termData?.precount != "2" || (termData?.contributions ?? 0) > 0) && (
                                            <div className="send-email-body-attachment">
                                                {!isPress && (
                                                    <CloseIcon
                                                        style={{
                                                            color: "grey",
                                                            width: "13px",
                                                            height: "13px",
                                                            cursor: "pointer",
                                                        }}
                                                        onClick={() =>
                                                            setIgnore((prev) => [...prev, EStatementType.PRECOUNT])
                                                        }
                                                    />
                                                )}
                                                {isBulk ? <PDF /> : isStatementCurrencyDefault && <PDF />}
                                                {isBulk ? (
                                                    <div
                                                        title={getStatementFilename(
                                                            "{{name}}",
                                                            "{{title}}",
                                                            t("SendEmail.Precount", { lng: emailLanguage }),
                                                            new Date(),
                                                        )}
                                                        className="send-email-body-attachment-filename"
                                                    >
                                                        {getStatementFilename(
                                                            "{{name}}",
                                                            "{{title}}",
                                                            t("SendEmail.Precount", { lng: emailLanguage }),
                                                            new Date(),
                                                        )}
                                                    </div>
                                                ) : (
                                                    isStatementCurrencyDefault && (
                                                        <div
                                                            title={getStatementFilename(
                                                                termData?.author,
                                                                termData?.title,
                                                                t("SendEmail.Precount", { lng: emailLanguage }),
                                                                new Date(),
                                                            )}
                                                            className="send-email-body-attachment-filename"
                                                        >
                                                            {getStatementFilename(
                                                                termData?.author,
                                                                termData?.title,
                                                                t("SendEmail.Precount", { lng: emailLanguage }),
                                                                new Date(),
                                                            )}
                                                        </div>
                                                    )
                                                )}
                                                {/* <div className="send-email-body-attachment-size">2mb</div> */}
                                            </div>
                                        )}
                                    {hasCollectiveManagement &&
                                        !ignore.includes(EStatementType.COLLECTIVE_MANAGEMENT) && (
                                            <div className="send-email-body-attachment">
                                                <CloseIcon
                                                    style={{
                                                        color: "grey",
                                                        width: "13px",
                                                        height: "13px",
                                                        cursor: "pointer",
                                                    }}
                                                    onClick={() =>
                                                        setIgnore((prev) => [
                                                            ...prev,
                                                            EStatementType.COLLECTIVE_MANAGEMENT,
                                                        ])
                                                    }
                                                />
                                                <PDF />
                                                <div
                                                    title={getStatementFilename(
                                                        termData?.author,
                                                        termData?.title,
                                                        t("SendEmail.CollectiveManagement", { lng: emailLanguage }),
                                                        new Date(),
                                                    )}
                                                    className="send-email-body-attachment-filename"
                                                >
                                                    {getStatementFilename(
                                                        termData?.author,
                                                        termData?.title,
                                                        t("SendEmail.CollectiveManagement", { lng: emailLanguage }),
                                                        new Date(),
                                                    )}
                                                </div>
                                            </div>
                                        )}
                                    {isPress && termData?.contractSigned === 2 && (
                                        <div className="send-email-body-attachment">
                                            <PDF />
                                            <div
                                                title={getStatementFilename(
                                                    termData?.author,
                                                    termData?.title,
                                                    t("SendEmail.Contract", { lng: emailLanguage }),
                                                    new Date(),
                                                )}
                                                className="send-email-body-attachment-filename"
                                            >
                                                {getStatementFilename(
                                                    termData?.author,
                                                    termData?.title,
                                                    t("SendEmail.Contract", { lng: emailLanguage }),
                                                    new Date(),
                                                )}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                        <div className="send-email-footer">
                            <div className="send-email-isBulk">{isBulk ? t("SendEmail.IsBulk") : ""}</div>
                            <div className="send-email-button">
                                <StyledButton
                                    buttonType="primary"
                                    onClick={() => {
                                        /**
                                         * If user edited subject/body, send their text as-is to all recipients.
                                         * Otherwise pass per-language defaults so each beneficiary receives in their own language.
                                         * Normalize with `?? ""` because `emailSubject`/`emailContent` can be `undefined`
                                         * when no company email template is configured — `subjectInput`/`bodyInput` are then
                                         * `""` and would spuriously look edited.
                                         */
                                        const userEdited =
                                            subjectInput !== (emailSubject ?? "") || bodyInput !== (emailContent ?? "");
                                        sendEmail(
                                            emails,
                                            subjectInput,
                                            bodyInput,
                                            ignore,
                                            userEdited ? undefined : subjectByLang,
                                            userEdited ? undefined : bodyByLang,
                                        );
                                        setIgnore([]);
                                    }}
                                    disabled={!isBulk && !termData.email}
                                >
                                    {t("SendEmail.Send")}
                                    <SendEmailIcon />
                                </StyledButton>
                            </div>
                        </div>
                    </div>
                </Modal>
            )}
        </>
    );
};

export default SendEmail;
